import { useEffect, useMemo, useRef } from 'react';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { Ticket } from '@/lib/api';
import type { StoredMessage, TicketState } from '../../../src/types';
import { Avatar } from '@/components/Avatar';
import { StatusBadge } from '@/components/StatusBadge';
import { CoreIcon } from '@/components/CoreIcon';

function formatDayDivider(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return 'Today';
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function MessageThread(props: {
  ticket: Ticket;
  messages: StoredMessage[];
  liveState: TicketState | null;
  loading?: boolean;
  onMarkResolved?: () => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [props.messages.length, props.liveState?.currentDraft?.id]);

  // Group messages by day for dividers.
  const grouped = useMemo(() => {
    const out: Array<{ day: string; items: StoredMessage[] }> = [];
    for (const m of props.messages) {
      const label = formatDayDivider(m.createdAt);
      if (out.length === 0 || out[out.length - 1].day !== label) {
        out.push({ day: label, items: [m] });
      } else {
        out[out.length - 1].items.push(m);
      }
    }
    return out;
  }, [props.messages]);

  const customerEmail = props.ticket.from_email;
  const customerName = customerEmail.split('@')[0].replace(/[._\-+]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      {/* Header — customer identity + status controls. */}
      <header className="px-5 py-3 border-b border-border bg-surface flex items-center gap-3">
        <Avatar seed={customerEmail} label={customerName} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold text-foreground truncate">
              {customerName}
            </h2>
            <StatusBadge status={props.ticket.status} />
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {customerEmail}
            {props.liveState?.category && <> · {props.liveState.category}</>}
            {props.liveState?.priority && props.liveState.priority !== 'normal' && (
              <> · priority {props.liveState.priority}</>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {props.ticket.status !== 'resolved' && (
            <button
              type="button"
              onClick={props.onMarkResolved}
              className="btn-ghost !h-8 !px-2.5 text-xs"
              title="Mark resolved"
            >
              <CoreIcon name="check-alt" size={14} />
              Resolve
            </button>
          )}
          <button
            type="button"
            className="btn-ghost !h-8 !w-8 !p-0"
            aria-label="More actions"
          >
            <CoreIcon name="options" size={16} />
          </button>
        </div>
      </header>

      {/* Conversation. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 bg-background">
        {grouped.length === 0 && props.loading && (
          <div className="space-y-5" role="status" aria-label="Loading messages">
            {/* Day divider skeleton */}
            <div className="flex items-center gap-3 my-3 animate-pulse">
              <div className="flex-1 h-px bg-muted" />
              <div className="h-2.5 w-12 bg-muted rounded-sm" />
              <div className="flex-1 h-px bg-muted" />
            </div>
            {/* Inbound message skeleton */}
            <div className="flex gap-3 items-start animate-pulse">
              <div className="h-7 w-7 rounded-full bg-muted shrink-0" />
              <div className="space-y-1.5 max-w-[65%]">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-20 bg-muted rounded-sm" />
                  <div className="h-2 w-10 bg-muted/60 rounded-sm" />
                </div>
                <div className="rounded-lg border border-muted/50 bg-muted/30 p-3 space-y-1.5">
                  <div className="h-2.5 w-full bg-muted rounded-sm" />
                  <div className="h-2.5 w-[85%] bg-muted rounded-sm" />
                  <div className="h-2.5 w-[60%] bg-muted rounded-sm" />
                </div>
              </div>
            </div>
            {/* Outbound message skeleton */}
            <div className="flex gap-3 items-start flex-row-reverse animate-pulse">
              <div className="h-7 w-7 rounded-full bg-muted shrink-0" />
              <div className="space-y-1.5 max-w-[65%] flex flex-col items-end">
                <div className="flex items-center gap-2 flex-row-reverse">
                  <div className="h-2.5 w-10 bg-muted rounded-sm" />
                  <div className="h-2 w-10 bg-muted/60 rounded-sm" />
                </div>
                <div className="rounded-lg bg-muted/50 p-3 space-y-1.5 w-full">
                  <div className="h-2.5 w-full bg-muted rounded-sm" />
                  <div className="h-2.5 w-[70%] bg-muted rounded-sm" />
                </div>
              </div>
            </div>
            {/* AI indicator skeleton */}
            <div className="flex items-center gap-2 animate-pulse">
              <div className="h-3.5 w-3.5 rounded bg-muted" />
              <div className="h-2.5 w-48 bg-muted rounded-sm" />
            </div>
          </div>
        )}
        {grouped.length === 0 && !props.loading && (
          <p className="text-sm text-muted-foreground">No messages yet.</p>
        )}
        {grouped.map((group) => (
          <div key={group.day} className="space-y-3.5 mb-6 last:mb-0">
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-border" />
              <span className="mono-label !text-[10.5px]">{group.day}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            {group.items.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </div>
        ))}

        {/* AI thinking indicator — visible while triage is running (no draft yet). */}
        {props.liveState && !props.liveState.currentDraft && props.liveState.status === 'open' && props.messages.length > 0 && (
          <div className="flex items-center gap-3 my-4 px-1" role="status" aria-label="AI is analyzing">
            <span className="text-accent"><CoreIcon name="sparkles" size={14} /></span>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-accent font-medium">AI is analyzing</span>
              <span className="flex gap-1 items-center">
                <span className="h-1 w-1 rounded-full bg-accent animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-1 w-1 rounded-full bg-accent animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-1 w-1 rounded-full bg-accent animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}

        {/* Inline system event — AI produced a draft awaiting review. */}
        {props.liveState?.currentDraft && (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground my-4">
            <span className="text-accent"><CoreIcon name="sparkles" size={14} /></span>
            <span>
              <span className="text-accent font-medium">AI drafted a reply</span>
              {props.liveState.currentDraft.body
                ? <>{' · '}confidence {Math.round((props.liveState.currentDraft.confidence ?? 0) * 100)}%{' · '}awaiting your review below</>
                : <>{' · '}could not generate a draft{' · '}write a manual reply or try AI Assist</>
              }
            </span>
          </div>
        )}

        <div ref={endRef} />
      </div>
    </>
  );
}

function MessageBubble({ message: m }: { message: StoredMessage }) {
  const inbound = m.direction === 'inbound';
  const displayName = inbound
    ? (m.fromEmail.split('@')[0].replace(/[._\-+]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    : 'You';

  const hasText = !!m.bodyText?.trim();
  const hasHtml = !!m.bodyHtml?.trim();

  return (
    <div className={cn('flex gap-3 items-start', !inbound && 'flex-row-reverse')}>
      <Avatar
        seed={inbound ? m.fromEmail : 'agent:self'}
        label={displayName}
        size={28}
        className="mt-1"
      />
      <div className={cn('min-w-0 max-w-[70%]', !inbound && 'items-end flex flex-col')}>
        <div className={cn(
          'flex items-center gap-2 mb-1 text-[11px] text-muted-foreground',
          !inbound && 'flex-row-reverse',
        )}>
          <span className="font-medium text-foreground">{displayName}</span>
          <span>{formatTime(m.createdAt)}</span>
        </div>
        <div
          className={cn(
            'rounded-lg px-3.5 py-2.5 text-[14px] leading-[1.5] break-words border',
            inbound
              ? 'bg-surface border-border'
              : 'bg-foreground text-surface border-foreground',
            !hasHtml && 'whitespace-pre-wrap',
          )}
        >
          {hasHtml ? (
            <div
              className="max-w-none text-inherit [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_p]:my-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.bodyHtml!) }}
            />
          ) : hasText ? (
            m.bodyText!.trim()
          ) : (
            '(no content)'
          )}
        </div>
      </div>
    </div>
  );
}

function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\bon\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\bon\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript\s*:/gi, '');
}
